import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarCheck, CheckCircle, Clock, ChevronDown, ChevronUp, Edit, FileText } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { getFromLocalStorage } from '../mock';

const BACKEND_URL = ''; // same-origin: reverse proxy routes /api/* to backend
const getHeaders = () => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${getFromLocalStorage('accessToken')}` });

const STATUS_LABELS = {
  planned: ['Planlandi', 'bg-blue-600'],
  reminded: ['Hatirlatildi', 'bg-yellow-600'],
  attended: ['Geldi', 'bg-green-600'],
  missed: ['Gelmedi', 'bg-red-600'],
};

const ControlAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [filter, setFilter] = useState('');
  const [showPast, setShowPast] = useState(false);
  const [editingApt, setEditingApt] = useState(null);

  useEffect(() => { loadAppointments(); }, []);

  const loadAppointments = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/control-appointments`, { headers: getHeaders() });
      if (res.ok) { const d = await res.json(); setAppointments(d.appointments || []); }
    } catch (e) { console.error('Load appointments error:', e); }
  };

  const updateAppointment = async (id, data) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/control-appointments/${id}`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Guncelleme basarisiz');
      const statusLabel = data.status ? (STATUS_LABELS[data.status]?.[0] || data.status) : 'Detaylar';
      toast({ title: "Guncellendi", description: `Randevu: ${statusLabel}` });
      loadAppointments();
      return true;
    } catch (e) { toast({ title: "Hata", description: e.message, variant: "destructive" }); return false; }
  };

  const today = new Date().toISOString().split('T')[0];
  const upcoming = appointments.filter(a => a.plannedDate >= today && a.status !== 'attended');
  const past = appointments.filter(a => a.plannedDate < today || a.status === 'attended');

  const filtered = (arr) => filter
    ? arr.filter(a => a.customerName?.toLowerCase().includes(filter.toLowerCase()) || a.carPlate?.includes(filter))
    : arr;

  const AppointmentCard = ({ apt }) => {
    const [label, cls] = STATUS_LABELS[apt.status] || STATUS_LABELS.planned;
    const isOverdue = apt.plannedDate < today && apt.status === 'planned';
    const hasDetails = apt.observations || apt.actionsTaken || apt.outcome;
    return (
      <Card className={`border-gray-700/40 ${isOverdue ? 'bg-red-900/20 border-red-700/40' : 'bg-gray-800/60'}`}
        data-testid={`control-card-${apt.id}`}>
        <CardContent className="p-3">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-white font-semibold text-sm">{apt.customerName}</p>
                <Badge className={`${cls} text-white text-[10px]`}>{label}</Badge>
                {isOverdue && <Badge className="bg-red-600 text-white text-[10px]">Gecikmis</Badge>}
                {hasDetails && <Badge className="bg-purple-600 text-white text-[10px]">Detay Var</Badge>}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                <span className="text-brand font-semibold">{apt.carPlate}</span>
                <span><CalendarCheck className="h-3 w-3 inline mr-1" />{new Date(apt.plannedDate).toLocaleDateString('tr-TR')}</span>
              </div>
              {apt.services?.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {apt.services.slice(0, 3).map((s, i) => (
                    <Badge key={`ctrl-svc-${s}-${i}`} variant="outline" className="border-gray-600 text-gray-300 text-[10px]">{s}</Badge>
                  ))}
                </div>
              )}
              {apt.notes && <p className="text-gray-500 text-xs mt-1">{apt.notes}</p>}
              {/* Show detail summary if present */}
              {hasDetails && (
                <div className="mt-1.5 bg-gray-900/60 rounded p-2 space-y-0.5">
                  {apt.observations && <p className="text-xs text-gray-300"><span className="text-gray-500">Gozlem:</span> {apt.observations}</p>}
                  {apt.actionsTaken && <p className="text-xs text-gray-300"><span className="text-gray-500">Yapilan:</span> {apt.actionsTaken}</p>}
                  {apt.outcome && <p className="text-xs text-gray-300"><span className="text-gray-500">Sonuc:</span> {apt.outcome}</p>}
                </div>
              )}
            </div>
            <div className="flex gap-1 flex-shrink-0 flex-col items-end">
              <div className="flex gap-1">
                {apt.status !== 'attended' && (
                  <Button size="sm" onClick={() => updateAppointment(apt.id, { status: 'attended' })}
                    className="bg-green-600 hover:bg-green-700 text-white text-xs h-7" data-testid={`mark-attended-${apt.id}`}>
                    <CheckCircle className="h-3 w-3 mr-1" />Geldi
                  </Button>
                )}
                {apt.status === 'planned' && (
                  <Button size="sm" onClick={() => updateAppointment(apt.id, { status: 'reminded' })}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white text-xs h-7">
                    Hatirlatildi
                  </Button>
                )}
                {apt.status === 'planned' && isOverdue && (
                  <Button size="sm" onClick={() => updateAppointment(apt.id, { status: 'missed' })}
                    className="bg-red-600 hover:bg-red-700 text-white text-xs h-7">
                    Gelmedi
                  </Button>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={() => setEditingApt(apt)}
                className="border-gray-600 text-gray-300 text-xs h-7 mt-1" data-testid={`edit-control-${apt.id}`}>
                <FileText className="h-3 w-3 mr-1" />Detay
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Input placeholder="Musteri veya plaka ara..." value={filter} onChange={e => setFilter(e.target.value)}
          className="bg-gray-800 border-gray-700 text-white h-8 text-sm max-w-xs" data-testid="control-search" />
        <div className="ml-auto flex items-center gap-2 text-xs text-gray-400">
          <Badge className="bg-blue-600 text-white">{upcoming.length}</Badge> Yaklasan
          <Badge className="bg-gray-600 text-white">{past.length}</Badge> Gecmis
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        <Card className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-2 text-center">
          <p className="text-2xl font-bold text-blue-400">{appointments.filter(a => a.status === 'planned').length}</p>
          <p className="text-gray-400 text-[10px]">Planlandi</p>
        </CardContent></Card>
        <Card className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-2 text-center">
          <p className="text-2xl font-bold text-yellow-400">{appointments.filter(a => a.status === 'reminded').length}</p>
          <p className="text-gray-400 text-[10px]">Hatirlatildi</p>
        </CardContent></Card>
        <Card className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-2 text-center">
          <p className="text-2xl font-bold text-green-400">{appointments.filter(a => a.status === 'attended').length}</p>
          <p className="text-gray-400 text-[10px]">Geldi</p>
        </CardContent></Card>
        <Card className="bg-gray-800/70 border-gray-700/40"><CardContent className="p-2 text-center">
          <p className="text-2xl font-bold text-red-400">{appointments.filter(a => a.status === 'missed').length}</p>
          <p className="text-gray-400 text-[10px]">Gelmedi</p>
        </CardContent></Card>
      </div>

      {/* Upcoming */}
      <div>
        <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
          <Clock className="h-4 w-4 text-blue-400" />Yaklasan Kontroller ({filtered(upcoming).length})
        </h3>
        {filtered(upcoming).length === 0
          ? <p className="text-gray-400 text-xs text-center py-4">Yaklasan kontrol randevusu yok</p>
          : <div className="space-y-2">{filtered(upcoming).map(a => <AppointmentCard key={a.id} apt={a} />)}</div>
        }
      </div>

      {/* Past */}
      <div>
        <Button size="sm" variant="outline" onClick={() => setShowPast(!showPast)}
          className="border-gray-600 text-gray-400 text-xs mb-2">
          {showPast ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
          Gecmis Kontroller ({past.length})
        </Button>
        {showPast && (
          <div className="space-y-2">
            {filtered(past).map(a => <AppointmentCard key={a.id} apt={a} />)}
          </div>
        )}
      </div>

      {/* Detail Editing Modal */}
      {editingApt && (
        <ControlDetailModal
          appointment={editingApt}
          onClose={() => setEditingApt(null)}
          onSave={async (data) => {
            const ok = await updateAppointment(editingApt.id, data);
            if (ok) setEditingApt(null);
          }}
        />
      )}
    </div>
  );
};

const ControlDetailModal = ({ appointment, onClose, onSave }) => {
  const [form, setForm] = useState({
    status: appointment.status || 'planned',
    notes: appointment.notes || '',
    observations: appointment.observations || '',
    actionsTaken: appointment.actionsTaken || '',
    outcome: appointment.outcome || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    await onSave(form);
    setLoading(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FileText className="h-5 w-5 text-brand" />
            Kontrol Detaylari - {appointment.customerName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-gray-800 rounded-lg p-3">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-brand font-bold">{appointment.carPlate}</span>
              <span className="text-gray-400">{new Date(appointment.plannedDate).toLocaleDateString('tr-TR')}</span>
              {appointment.services?.length > 0 && (
                <span className="text-gray-500 text-xs">{appointment.services.join(', ')}</span>
              )}
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-xs mb-1 block">Durum</label>
            <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="control-detail-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                <SelectItem value="planned" className="text-white hover:bg-gray-800">Planlandi</SelectItem>
                <SelectItem value="reminded" className="text-white hover:bg-gray-800">Hatirlatildi</SelectItem>
                <SelectItem value="attended" className="text-white hover:bg-gray-800">Geldi</SelectItem>
                <SelectItem value="missed" className="text-white hover:bg-gray-800">Gelmedi</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-gray-400 text-xs mb-1 block">Notlar</label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2} placeholder="Kontrol notlari..."
              className="bg-gray-800 border-gray-700 text-white text-sm" data-testid="control-detail-notes" />
          </div>

          <div>
            <label className="text-gray-400 text-xs mb-1 block">Gozlemler</label>
            <Textarea value={form.observations} onChange={e => setForm(f => ({ ...f, observations: e.target.value }))}
              rows={2} placeholder="Arac uzerindeki gozlemler..."
              className="bg-gray-800 border-gray-700 text-white text-sm" data-testid="control-detail-observations" />
          </div>

          <div>
            <label className="text-gray-400 text-xs mb-1 block">Yapilan Islemler</label>
            <Textarea value={form.actionsTaken} onChange={e => setForm(f => ({ ...f, actionsTaken: e.target.value }))}
              rows={2} placeholder="Yapilan islemler..."
              className="bg-gray-800 border-gray-700 text-white text-sm" data-testid="control-detail-actions" />
          </div>

          <div>
            <label className="text-gray-400 text-xs mb-1 block">Sonuc / Durum</label>
            <Textarea value={form.outcome} onChange={e => setForm(f => ({ ...f, outcome: e.target.value }))}
              rows={2} placeholder="Kontrol sonucu..."
              className="bg-gray-800 border-gray-700 text-white text-sm" data-testid="control-detail-outcome" />
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={handleSave} disabled={loading} className="flex-1 bg-brand hover:bg-brand" data-testid="save-control-detail-btn">
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
            <Button onClick={onClose} variant="outline" className="border-gray-600 text-gray-300">Iptal</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ControlAppointments;
